import * as React from 'react';

import { useMutation } from '@apollo/client';

import {
  VoteType,
  VoteAction,
  HandleVoteMutation,
  HandleVoteMutationVariables,
  GetReviewsQuery,
} from '@notino/shared/definitions/types';

import { RequestState } from '@containers/App/model';
import handleVoteMutationQuery from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/queries/handleVote.graphql';
import { canBeVoted } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/utils';

export const useReviewVote = (
  item:
    | NonNullable<
        NonNullable<GetReviewsQuery['reviewsAndFilters']>['data']
      >['reviews'][number]
    | null
) => {
  const [
    { [VoteType.Like]: likeLoading, [VoteType.Dislike]: dislikeLoading },
    setState,
  ] = React.useState({
    [VoteType.Like]: RequestState.DEFAULT,
    [VoteType.Dislike]: RequestState.DEFAULT,
  });

  const [handleVoteMutation] = useMutation<
    HandleVoteMutation,
    HandleVoteMutationVariables
  >(handleVoteMutationQuery);

  const handleVote = async (type: VoteType) => {
    if (!item) return;

    const unvote =
      (item.alreadyLiked && type === VoteType.Like) ||
      (item.alreadyDisliked && type === VoteType.Dislike);

    const otherType = type === VoteType.Like ? VoteType.Dislike : VoteType.Like;

    setState((old) => ({
      ...old,
      [type]: RequestState.IN_PROGRESS,
      [otherType]: RequestState.DEFAULT,
    }));

    try {
      await handleVoteMutation({
        variables: {
          id: item.id.toString(),
          type,
          action: unvote ? VoteAction.Remove : VoteAction.Add,
        },
      });

      setState((old) => ({
        ...old,
        [type]: RequestState.DONE,
      }));
    } catch (e) {
      setState((old) => ({
        ...old,
        [type]: RequestState.FAILED,
      }));
    }
  };

  const like = async () => {
    if (
      item &&
      canBeVoted(item, VoteType.Like) &&
      likeLoading !== RequestState.IN_PROGRESS
    ) {
      await handleVote(VoteType.Like);
    }
  };

  const dislike = async () => {
    if (
      item &&
      canBeVoted(item, VoteType.Dislike) &&
      dislikeLoading !== RequestState.IN_PROGRESS
    ) {
      await handleVote(VoteType.Dislike);
    }
  };

  return {
    like,
    dislike,
    likeLoading,
    dislikeLoading,
  };
};
